import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '../shared/components/Icon';
import { useIsMobileShell } from '../shared/lib/responsive';
import { DockDragProvider } from './dockDrag';
import { DockLayout } from './DockLayout';
import { DockOverlay } from './DockOverlay';
import { DocumentPool } from './DocumentPool';
import { findGroup } from './dockModel';
import { FloatingWindows } from './FloatingWindows';
import { canonicalAddress, consoleRoot, describeAddress } from './panelCatalog';
import { useDockElements, useWorkspace, useWorkspaceActions } from './WorkspaceProvider';
import { activeAddress, activePanelId, findPanelByAddress, panelHostId } from './workspaceState';

/**
 * The workspace itself: one canvas holding every open document.
 *
 * Two rules keep it honest. The browser address bar always shows whichever document is
 * in front, so a workspace can still be linked to and reloaded; and the arrangement is
 * the operator's, not the application's — nothing here rearranges panes on its own.
 */
export function WorkspaceCanvas() {
  const isMobile = useIsMobileShell();
  return (
    <DockDragProvider>
      {isMobile ? <MobileWorkspace /> : <DesktopWorkspace />}
    </DockDragProvider>
  );
}

/** Keeps the browser address on the document in front, in both directions. */
function useAddressBarBridge() {
  const state = useWorkspace();
  const { dispatch } = useWorkspaceActions();
  const location = useLocation();
  const navigate = useNavigate();
  const address = activeAddress(state);
  const browserAddress = canonicalAddress(`${location.pathname}${location.search}`);
  const latestState = useRef(state);
  const previous = useRef({ address, browserAddress });
  latestState.current = state;

  /*
   * An address that changed on its own came from inside the document in front — a row
   * followed into its record, a filter written into the query — because every other
   * document is inert while it waits. So the front tab follows it, the way a browser tab
   * follows a link, unless the address is already open somewhere else, in which case that
   * tab is brought forward rather than a second copy made.
   *
   * The navigation rail, the palette, and the start page never come through here: they
   * ask the workspace to open a document outright, which is what makes them add a tab.
   */
  useEffect(() => {
    const before = previous.current;
    const browserChanged = browserAddress !== before.browserAddress;
    const workspaceChanged = address !== before.address;
    previous.current = { address, browserAddress };

    if (browserAddress === address) return;

    // Bringing another workspace tab forward changes only the workspace address. Reflect
    // that in browser history without letting the location-to-workspace path fight it.
    if (workspaceChanged && !browserChanged) {
      if (address !== null) void navigate(address, { replace: true });
      return;
    }

    // A link, filter, or query-string control inside the front document changes only the
    // browser address. Make the front tab follow it. On initial/concurrent disagreement,
    // the visible browser address wins because it is the user's explicit navigation.
    if (!browserAddress.startsWith(consoleRoot)) return;
    const current = latestState.current;
    const alreadyOpen = findPanelByAddress(current, browserAddress);
    if (alreadyOpen !== undefined) {
      dispatch({ type: 'activate', panelId: alreadyOpen.id });
      return;
    }
    const frontId = activePanelId(current);
    if (frontId === null) dispatch({ type: 'open', address: browserAddress });
    else dispatch({ type: 'navigated', panelId: frontId, address: browserAddress });
  }, [address, browserAddress, dispatch, navigate]);
}

/** Alt is the workspace's own modifier: the browser claims Ctrl for its window and tabs. */
function useWorkspaceShortcuts() {
  const state = useWorkspace();
  const { dispatch } = useWorkspaceActions();

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!event.altKey || event.ctrlKey || event.metaKey) return;
      const target = event.target;
      // Alt with an arrow moves the caret by word in a field; the workspace stays out of it.
      if (target instanceof HTMLElement && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))) return;
      const panelId = activePanelId(state);
      const key = event.key.toLocaleLowerCase();

      if (key === 'w' && panelId !== null) {
        event.preventDefault();
        dispatch({ type: 'close', panelId });
        return;
      }
      if (key === 'm') {
        const hostId = panelId === null ? null : panelHostId(state, panelId);
        if (hostId !== null && findGroup(state.root, hostId) !== undefined) {
          event.preventDefault();
          dispatch({ type: 'toggleMaximize', groupId: hostId });
        }
        return;
      }
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      const hostId = panelId === null ? null : panelHostId(state, panelId);
      const group = hostId === null ? undefined : findGroup(state.root, hostId);
      const siblings = group?.panelIds ?? state.windows.find((window) => window.id === hostId)?.panelIds;
      if (siblings === undefined || siblings.length < 2 || panelId === null) return;
      event.preventDefault();
      const step = event.key === 'ArrowRight' ? 1 : -1;
      const next = siblings[(siblings.indexOf(panelId) + step + siblings.length) % siblings.length];
      if (next !== undefined) dispatch({ type: 'activate', panelId: next });
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [dispatch, state]);
}

function DesktopWorkspace() {
  const { t } = useTranslation();
  const state = useWorkspace();
  const { dispatch } = useWorkspaceActions();
  const { setCanvas } = useDockElements();
  const canvas = useRef<HTMLDivElement | null>(null);
  useAddressBarBridge();
  useWorkspaceShortcuts();

  const attachCanvas = useCallback((element: HTMLDivElement | null) => {
    canvas.current = element;
    setCanvas(element);
    return () => setCanvas(null);
  }, [setCanvas]);

  // A narrower canvas — the rail expanding, the browser resizing — must not strand a
  // window off the edge where nothing can reach it again.
  const windows = state.windows;
  useEffect(() => {
    const element = canvas.current;
    if (element === null || windows.length === 0 || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => {
      if (entry === undefined) return;
      const { width, height } = entry.contentRect;
      for (const window of windows) {
        if (window.state === 'minimized') continue;
        const nextWidth = Math.min(window.rect.width, Math.max(width - 16, 280));
        const nextHeight = Math.min(window.rect.height, Math.max(height - 16, 180));
        const x = Math.max(0, Math.min(window.rect.x, width - nextWidth));
        const y = Math.max(0, Math.min(window.rect.y, height - nextHeight));
        if (x === window.rect.x && y === window.rect.y && nextWidth === window.rect.width && nextHeight === window.rect.height) continue;
        dispatch({ type: 'moveWindow', windowId: window.id, rect: { x, y, width: nextWidth, height: nextHeight } });
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [dispatch, windows]);

  return (
    <div
      className={`workspace-canvas${state.maximizedGroupId === null ? '' : ' workspace-canvas--maximized'}`}
      ref={attachCanvas}
      aria-label={t('workspace.canvas')}
    >
      <DockLayout node={state.root} />
      <FloatingWindows />
      <DocumentPool />
      <DockOverlay />
    </div>
  );
}

/**
 * On a phone there is room for one document at a time, so the same open set is presented
 * as a single strip: no panes, no windows, and no dragging — but nothing is lost, and the
 * arrangement made on a desktop is still there when the same account returns to one.
 */
function MobileWorkspace() {
  const { t } = useTranslation();
  const state = useWorkspace();
  const { dispatch } = useWorkspaceActions();
  const [body, setBody] = useState<HTMLElement | null>(null);
  useAddressBarBridge();

  const front = activePanelId(state);
  const documents = Object.values(state.panels);

  return (
    <div className="workspace-canvas workspace-canvas--mobile">
      {documents.length < 2 ? null : (
        <div className="dock-tabs dock-tabs--mobile" role="tablist" aria-label={t('workspace.openDocuments')}>
          {documents.map((panel) => {
            const identity = describeAddress(panel.address, t);
            const isActive = panel.id === front;
            return (
              <div
                key={panel.id}
                role="tab"
                tabIndex={isActive ? 0 : -1}
                aria-selected={isActive}
                aria-controls={`dock-document-${panel.id}`}
                className={`dock-tab${isActive ? ' dock-tab--active' : ''}`}
                onClick={() => dispatch({ type: 'activate', panelId: panel.id })}
              >
                <Icon name={identity.icon} className="icon icon--small dock-tab__icon" />
                <span className="dock-tab__label">{identity.title}</span>
                <button
                  type="button"
                  className="dock-tab__close"
                  aria-label={t('workspace.closeNamedDocument', { name: identity.title })}
                  onClick={(event) => {
                    event.stopPropagation();
                    dispatch({ type: 'close', panelId: panel.id });
                  }}
                >
                  <Icon name="close" className="icon icon--small" />
                </button>
              </div>
            );
          })}
        </div>
      )}
      <div className="workspace-canvas__mobile-body" ref={setBody} />
      <DocumentPool soloHost={body} soloPanelId={front} />
    </div>
  );
}
